/**
 * Onboarding Invite Tokens — IN-2.a
 * ─────────────────────────────────
 * Pure writers for opaque single-use invite tokens.
 *
 * Hard rules:
 *   • Token is opaque, >=32 chars, no PII embedded.
 *   • Default expiry 14 days.
 *   • Single-use. Consumed/revoked/expired tokens cannot be re-consumed.
 *   • No email sending — caller renders a copyable URL only.
 */

import {
  DEFAULT_INVITE_EXPIRY_DAYS,
  MIN_REASON_LENGTH,
  MIN_TOKEN_LENGTH,
  type OnboardingEntityType,
  type OnboardingInviteToken,
} from "./types";

function nowIso(): string {
  return new Date().toISOString();
}

function requireNonEmpty(v: string | undefined, field: string): string {
  const t = (v ?? "").trim();
  if (!t) throw new Error(`onboardingInviteTokens: ${field} is required.`);
  return t;
}

let __seq = 0;
function nextId(): string {
  __seq += 1;
  return `oit-${Date.now().toString(36)}-${__seq.toString(36)}`;
}

/** Opaque random token (hex), >=32 chars. No PII. */
export function generateOpaqueToken(byteLen = 24): string {
  let raw = "";
  const g =
    typeof globalThis !== "undefined"
      ? (globalThis as { crypto?: Crypto }).crypto
      : undefined;
  if (g?.getRandomValues) {
    const buf = new Uint8Array(byteLen);
    g.getRandomValues(buf);
    raw = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
  } else {
    while (raw.length < byteLen * 2) {
      raw += Math.random().toString(16).slice(2);
    }
    raw = raw.slice(0, byteLen * 2);
  }
  // Pad to at least MIN_TOKEN_LENGTH if needed.
  while (raw.length < MIN_TOKEN_LENGTH) raw += "0";
  return raw;
}

export interface CreateInviteTokenInput {
  entityType: OnboardingEntityType;
  contractorId?: string;
  parentId?: string;
  expiresAt?: string;
}

/**
 * IN-2.f — entity types that require a parent binding on the invite
 * token. Tokens without a parentId are rejected; the wizard cannot
 * start an orphan child session.
 */
const CHILD_ENTITY_TYPES: ReadonlySet<OnboardingEntityType> = new Set([
  "branch",
  "crew",
  "helper",
]);

export function createInviteToken(
  tokens: OnboardingInviteToken[],
  input: CreateInviteTokenInput,
  actor: string,
): { tokens: OnboardingInviteToken[]; token: OnboardingInviteToken } {
  const createdBy = requireNonEmpty(actor, "actor");
  if (!input.entityType) {
    throw new Error("onboardingInviteTokens: entityType is required.");
  }
  // IN-2.f — child entities cannot be invited without a parent binding.
  if (CHILD_ENTITY_TYPES.has(input.entityType)) {
    const pid = (input.parentId ?? "").trim();
    if (!pid) {
      throw new Error(
        `onboardingInviteTokens: parentId is required for ${input.entityType} invites.`,
      );
    }
    if (input.contractorId && input.contractorId === pid) {
      throw new Error(
        "onboardingInviteTokens: child entity cannot be its own parent.",
      );
    }
  }
  const createdAt = nowIso();
  const expiresAt =
    input.expiresAt ??
    new Date(
      Date.now() + DEFAULT_INVITE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    ).toISOString();
  const token: OnboardingInviteToken = {
    id: nextId(),
    token: generateOpaqueToken(),
    contractorId: input.contractorId,
    parentId: input.parentId,
    entityType: input.entityType,
    createdBy,
    createdAt,
    expiresAt,
    status: "pending",
  };
  return { tokens: [...tokens, token], token };
}

export function revokeInviteToken(
  tokens: OnboardingInviteToken[],
  tokenId: string,
  actor: string,
  reason: string,
): OnboardingInviteToken[] {
  const revokedBy = requireNonEmpty(actor, "actor");
  const r = (reason ?? "").trim();
  if (r.length < MIN_REASON_LENGTH) {
    throw new Error(
      `onboardingInviteTokens: reason must be at least ${MIN_REASON_LENGTH} chars.`,
    );
  }
  const target = tokens.find((t) => t.id === tokenId);
  if (!target) {
    throw new Error(`onboardingInviteTokens: token ${tokenId} not found.`);
  }
  if (target.status === "consumed") {
    throw new Error(
      `onboardingInviteTokens: token ${tokenId} already consumed; cannot revoke.`,
    );
  }
  if (target.status === "revoked") return tokens;
  return tokens.map((t) =>
    t.id === tokenId
      ? { ...t, status: "revoked", revokedAt: nowIso(), revokedBy, revokedReason: r }
      : t,
  );
}

export interface ConsumeInviteTokenResult {
  tokens: OnboardingInviteToken[];
  token: OnboardingInviteToken;
}

export function consumeInviteToken(
  tokens: OnboardingInviteToken[],
  tokenString: string,
  consumedBy?: string,
  at: string = nowIso(),
): ConsumeInviteTokenResult {
  const target = tokens.find((t) => t.token === tokenString);
  if (!target) {
    throw new Error("onboardingInviteTokens: invite token not found.");
  }
  if (target.status === "revoked") {
    throw new Error("onboardingInviteTokens: invite token has been revoked.");
  }
  if (target.status === "consumed") {
    throw new Error("onboardingInviteTokens: invite token has already been used.");
  }
  if (Date.parse(target.expiresAt) <= Date.parse(at)) {
    // Mark as expired (state derivation) but reject.
    const updated = tokens.map((t) =>
      t.id === target.id ? { ...t, status: "expired" as const } : t,
    );
    throw Object.assign(
      new Error("onboardingInviteTokens: invite token has expired."),
      { tokens: updated },
    );
  }
  const consumed: OnboardingInviteToken = {
    ...target,
    status: "consumed",
    consumedAt: at,
    consumedBy: consumedBy?.trim() || undefined,
  };
  return {
    tokens: tokens.map((t) => (t.id === target.id ? consumed : t)),
    token: consumed,
  };
}

/** Copyable URL helper — caller decides path. No email side-effects. */
export function buildInviteUrl(origin: string, token: string, path = "/onboarding"): string {
  const base = origin.replace(/\/$/, "");
  return `${base}${path}?invite=${encodeURIComponent(token)}`;
}

/** Pure derivation: is the token usable right now? */
export function isInviteTokenUsable(
  t: OnboardingInviteToken,
  at: string = nowIso(),
): boolean {
  if (t.status !== "pending") return false;
  return Date.parse(t.expiresAt) > Date.parse(at);
}

/**
 * IN-2.h — derived expiry view for UX display. Pure; no side effects.
 * Returns whole-day countdown (negative when past), and a coarse
 * `state` the wizard / admin surfaces can render directly.
 */
export interface InviteTokenExpiryView {
  state: "active" | "expiring_soon" | "expired" | "revoked" | "consumed";
  daysRemaining: number;
}

export function getInviteTokenExpiryView(
  t: OnboardingInviteToken,
  at: string = nowIso(),
  warnWithinDays = 2,
): InviteTokenExpiryView {
  if (t.status === "revoked") return { state: "revoked", daysRemaining: 0 };
  if (t.status === "consumed") return { state: "consumed", daysRemaining: 0 };
  const dayMs = 24 * 60 * 60 * 1000;
  const remainingMs = Date.parse(t.expiresAt) - Date.parse(at);
  const daysRemaining = Math.floor(remainingMs / dayMs);
  if (remainingMs <= 0 || t.status === "expired") {
    return { state: "expired", daysRemaining };
  }
  if (daysRemaining <= warnWithinDays) {
    return { state: "expiring_soon", daysRemaining };
  }
  return { state: "active", daysRemaining };
}
